import { useEffect, useRef } from 'react';

import { applyChatScroll, resolveChatScroll } from '../utils/chatScroll';

/**
 * Owns chat auto-scroll: resolves what scroll action (if any) each update
 * calls for and applies it immediately plus once deferred, so freshly added
 * messages settle into place. Returns the refs the caller attaches to the
 * message region container and the end-of-transcript anchor.
 *
 * `isTyping` is a deliberate effect dependency even though the effect body
 * never reads it: the typing indicator appearing or disappearing changes
 * the region's height, so the scroll must re-resolve on that transition.
 * Do not "clean it up".
 */
export default function useChatScroll({ errorMessage, isTyping, messages }) {
  const messageRegionRef = useRef(null);
  const messageEndRef = useRef(null);
  const prevMessageCountRef = useRef(0);

  useEffect(() => {
    const messageRegion = messageRegionRef.current;
    const messageEnd = messageEndRef.current;

    const previousMessageCount = prevMessageCountRef.current;
    prevMessageCountRef.current = messages.length;

    // Resolve the scroll action once (no DOM reads) so the immediate and
    // deferred passes stay consistent as freshly added messages settle.
    const scrollAction = resolveChatScroll({
      errorMessage,
      messages,
      previousMessageCount,
    });

    if (!messageRegion || !scrollAction) {
      return () => {};
    }

    const applyScroll = () =>
      applyChatScroll(scrollAction, { messageEnd, messageRegion });

    applyScroll();
    const deferredTimer = setTimeout(applyScroll, 120);

    return () => {
      clearTimeout(deferredTimer);
    };
  }, [errorMessage, isTyping, messages]);

  return { messageEndRef, messageRegionRef };
}
